import React, { useCallback, useContext, useEffect, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Grid, Snackbar } from "@mui/material";

import { TranslationStatus } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/translations_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import { downloadTranslationToTemplate } from "api/endpoints/seo-templates";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";

interface TranslationRecordProps {
  row: TranslationStatus;
  crowdinFileId: number;
  getCrowdinFileList: () => void;
}

const TranslationRecord = (props: TranslationRecordProps) => {
  const { row, crowdinFileId, getCrowdinFileList } = props;
  const [loading, setLoading] = useState(false);
  const [buttonStatus, setButtonStatus] = useState(true);
  const { showError } = useContext(SnackbarContext);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const downloadTemplateTranslation = (
    crowdinFileId: number,
    languageId: string
  ) => {
    setLoading(true);
    downloadTranslationToTemplate(
      {
        crowdinFileId,
        languageId,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE, crowdinFileId.toString())
    )
      .then(() => {
        setDownloadSuccess(true);
        getCrowdinFileList();
      })
      .catch(showError)
      .finally(() => {
        setLoading(false);
      });
  };

  const updateButtonStatus = useCallback((progress: number) => {
    if (progress === 100) {
      setButtonStatus(false);
    }
  }, []);

  useEffect(() => {
    updateButtonStatus(row.approvalProgress);
  }, [updateButtonStatus, row.approvalProgress]);

  return (
    <>
      <Grid container>
        <Grid item xs={1} />
        <Grid
          item
          xs={10}
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          <b>{row.languageId.toUpperCase()}</b>
          <span style={{ minWidth: "11rem" }}>
            Translation Progress: {row.translationProgress}%
            <br />
            Approval Progress: {row.approvalProgress}%
          </span>
          <LoadingButton
            color="primary"
            onClick={() => {
              downloadTemplateTranslation(crowdinFileId, row.languageId);
            }}
            loading={loading}
            disabled={buttonStatus}
            loadingPosition="center"
            variant="contained"
            data-testid="download-translation-btn"
          >
            Import
          </LoadingButton>
        </Grid>
        <Grid item xs={1} />
      </Grid>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={downloadSuccess}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setDownloadSuccess(false)}
        data-testid="template-download-success"
      >
        <Alert severity="success" variant="filled">
          Template Strings Successfully Downloaded from Crowdin
        </Alert>
      </Snackbar>
    </>
  );
};

export default TranslationRecord;
